'use client';

import { createContext, Dispatch, ReactNode, SetStateAction, useContext, useState } from 'react';

export type MoviesListSortOption = {
  displayName: string;
  id: string;
  selected: boolean;
};

type MoviesListState = {
  filters: { [key: string]: boolean; };
  hasClearedLocationFilter: boolean;
  setFilters: Dispatch<SetStateAction<{ [key: string]: boolean; }>>;
  setHasClearedLocationFilter: Dispatch<SetStateAction<boolean>>;
  setSortOptions: Dispatch<SetStateAction<MoviesListSortOption[]>>;
  sortOptions: MoviesListSortOption[];
};

type MoviesListStateProviderProps = {
  children: ReactNode;
};

const defaultMoviesListSortOptions: MoviesListSortOption[] = [
  { displayName: 'Popular', id: 'popular', selected: true },
  { displayName: 'Release Date', id: 'release-date', selected: false },
  { displayName: 'Alphabetical', id: 'alphabetical', selected: false },
];

const MoviesListStateContext = createContext<MoviesListState | undefined>(undefined);

export const MoviesListStateProvider = ({ children }: Readonly<MoviesListStateProviderProps>): React.JSX.Element => {
  const [filters, setFilters] = useState<{ [key: string]: boolean; }>({});
  const [hasClearedLocationFilter, setHasClearedLocationFilter] = useState(false);
  const [sortOptions, setSortOptions] = useState<MoviesListSortOption[]>(defaultMoviesListSortOptions);

  return (
    <MoviesListStateContext.Provider value={{ filters, hasClearedLocationFilter, setFilters, setHasClearedLocationFilter, setSortOptions, sortOptions }}>
      {children}
    </MoviesListStateContext.Provider>
  );
};

export const useMoviesListState = (): MoviesListState => {
  const context = useOptionalMoviesListState();

  if (!context) {
    throw new Error('useMoviesListState must be used within MoviesListStateProvider.');
  }

  return context;
};

export const useOptionalMoviesListState = (): MoviesListState | undefined => {
  return useContext(MoviesListStateContext);
};
